import { LiquidGlassView, isLiquidGlassSupported } from '@callstack/liquid-glass';
import React, { createContext, memo, useContext, useMemo, type FC, type ReactNode } from 'react';
import {
  Pressable,
  type ColorValue,
  type GestureResponderEvent,
  type PressableProps,
  type PressableStateCallbackType,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import type { AvailableIconName } from '@/assets/icons';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text, type TextProps, type TextVariant } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';

/** Visual style of the button surface. */
export type PressableSurfaceVariant = 'solid' | 'glass';

/** Translucent fallback when native iOS Liquid Glass isn't available (Android / iOS < 26). */
const GLASS_FALLBACK_BACKGROUND = 'rgba(255, 255, 255, 1)';

type PressableSurfaceContextValue = {
  disabled: boolean;
};

const PressableSurfaceContext = createContext<PressableSurfaceContextValue | null>(null);

const usePressableSurfaceContext = () => {
  const ctx = useContext(PressableSurfaceContext);
  if (!ctx) {
    throw new Error(
      'PressableSurface subcomponents must be used within a <PressableSurface> root component.',
    );
  }
  return ctx;
};

export type PressableSurfaceProps = {
  /** Content of the button — typically `<PressableSurface.Icon />` and/or `<PressableSurface.Text />`. */
  children: ReactNode;
  onPress?: (event: GestureResponderEvent) => void;
  /**
   * Square size in design px (auto-scaled). Default 40.
   * Used for both width and height unless overridden by `width` / `height`.
   */
  size?: number;
  /** Override width in design px (auto-scaled). Defaults to `size`. */
  width?: number;
  /** Override height in design px (auto-scaled). Defaults to `size`. */
  height?: number;
  /** Background color. Defaults to transparent (no background). */
  backgroundColor?: string;
  /** Corner radius in design px (auto-scaled). Defaults to a full circle (height / 2). */
  borderRadius?: number;
  /**
   * Surface style. `'glass'` renders an iOS Liquid Glass material behind the content
   * (falls back to a translucent background on Android / iOS < 26). Default `'solid'`.
   */
  variant?: PressableSurfaceVariant;
  /** Liquid glass material variant. Only applies when `variant="glass"`. Default `'regular'`. */
  glassEffect?: 'clear' | 'regular';
  /** Tint color of the glass material. Only applies when `variant="glass"`. */
  glassTintColor?: ColorValue;
  /** Color scheme of the glass material. Only applies when `variant="glass"`. Default `'system'`. */
  glassColorScheme?: 'light' | 'dark' | 'system';
  /** Glass grows / shimmers on touch. Only applies when `variant="glass"`. Default `true`. */
  interactive?: boolean;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  hitSlop?: PressableProps['hitSlop'];
  accessibilityLabel?: string;
};

/**
 * PressableSurface – a sized, tappable surface. Circular by default; pass `width`/`height` +
 * `borderRadius` for pill / rounded shapes (e.g. social auth buttons), and `variant="glass"`
 * for an iOS Liquid Glass material. Compose its content with `<PressableSurface.Icon />`
 * and/or `<PressableSurface.Text />`.
 *
 * @example
 * <PressableSurface size={40} variant="glass" onPress={goBack}>
 *   <PressableSurface.Icon name="IcBackBlue" color="#64748B" />
 * </PressableSurface>
 */
const PressableSurfaceRoot: FC<PressableSurfaceProps> = ({
  children,
  onPress,
  size = 40,
  width,
  height,
  backgroundColor,
  borderRadius,
  variant = 'solid',
  glassEffect = 'regular',
  glassTintColor,
  glassColorScheme = 'system',
  interactive = true,
  disabled = false,
  style,
  hitSlop,
  accessibilityLabel,
}) => {
  const { scale } = useScaling();
  const styles = useStyles();

  const isGlass = variant === 'glass';
  /** Native glass paints the surface; only fall back to a translucent color when unsupported. */
  const showGlassFallback = isGlass && !isLiquidGlassSupported;

  const resolvedRadius = useMemo(() => {
    const resolvedHeight = scale(height ?? size);
    return borderRadius !== undefined ? scale(borderRadius) : resolvedHeight / 2;
  }, [scale, height, size, borderRadius]);

  const containerStyle = useMemo<ViewStyle>(() => {
    const resolvedBackground = isGlass
      ? showGlassFallback
        ? (backgroundColor ?? GLASS_FALLBACK_BACKGROUND)
        : undefined
      : backgroundColor;
    return {
      width: scale(width ?? size),
      height: scale(height ?? size),
      borderRadius: resolvedRadius,
      ...(isGlass && { overflow: 'hidden' }),
      ...(resolvedBackground !== undefined && { backgroundColor: resolvedBackground }),
    };
  }, [scale, width, height, size, resolvedRadius, isGlass, showGlassFallback, backgroundColor]);

  const ctxValue = useMemo<PressableSurfaceContextValue>(() => ({ disabled }), [disabled]);

  if (isGlass) {
    // Wrap the content directly in the glass surface so the material follows the
    // content box. The Pressable stays a transparent touch target around it.
    const getGlassPressableStyle = ({
      pressed,
    }: PressableStateCallbackType): StyleProp<ViewStyle> => [
      style,
      (disabled || pressed) && styles.dimmed,
    ];
    return (
      <PressableSurfaceContext.Provider value={ctxValue}>
        <Pressable
          onPress={onPress}
          disabled={disabled}
          hitSlop={hitSlop}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ disabled }}
          style={getGlassPressableStyle}>
          <LiquidGlassView
            style={[styles.base, containerStyle]}
            // Native glass props are no-ops on the plain-View fallback, so only
            // pass them when the platform actually supports the material.
            {...(showGlassFallback
              ? null
              : {
                  effect: glassEffect,
                  interactive,
                  tintColor: glassTintColor,
                  colorScheme: glassColorScheme,
                })}>
            {children}
          </LiquidGlassView>
        </Pressable>
      </PressableSurfaceContext.Provider>
    );
  }

  const getPressableStyle = ({ pressed }: PressableStateCallbackType): StyleProp<ViewStyle> => [
    styles.base,
    containerStyle,
    style,
    (disabled || pressed) && styles.dimmed,
  ];

  return (
    <PressableSurfaceContext.Provider value={ctxValue}>
      <Pressable
        onPress={onPress}
        disabled={disabled}
        hitSlop={hitSlop}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled }}
        style={getPressableStyle}>
        {children}
      </Pressable>
    </PressableSurfaceContext.Provider>
  );
};

export type PressableSurfaceIconProps = {
  /** Icon rendered inside the button. */
  name: AvailableIconName;
  /** Icon size in design px (auto-scaled). Default 24. */
  size?: number;
  /**
   * Icon color. Omit to keep the SVG's own colors
   * (e.g. multi-color brand icons like Google).
   */
  color?: string;
  /** Extra style for the icon itself, e.g. `{ transform: [{ scaleX: -1 }] }` to mirror it. */
  style?: StyleProp<ViewStyle>;
};

const PressableSurfaceIcon: FC<PressableSurfaceIconProps> = ({ name, size = 24, color, style }) => {
  usePressableSurfaceContext();
  const { scale } = useScaling();
  return <SvgIcon name={name} size={scale(size)} color={color} style={style} />;
};

export type PressableSurfaceTextProps = TextProps & {
  children: ReactNode;
  /** Typography variant. Default `'Font Inter/Subtitle/Medium 12px'`. */
  variant?: TextVariant;
};

const PressableSurfaceText: FC<PressableSurfaceTextProps> = ({
  children,
  variant = 'Font Inter/Subtitle/Medium 12px',
  ...rest
}) => {
  usePressableSurfaceContext();
  return (
    <Text variant={variant} {...rest}>
      {children}
    </Text>
  );
};

const useStyles = createStyles(({ scale }) => {
  return {
    base: {
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: scale(8),
    },
    dimmed: {
      opacity: 0.6,
    },
  };
});

export const PressableSurface = Object.assign(memo(PressableSurfaceRoot), {
  Icon: PressableSurfaceIcon,
  Text: PressableSurfaceText,
});

export default PressableSurface;
